/**
 * Unified OAuth Callback Handler
 * 
 * Single source of truth for ALL OAuth callback processing logic.
 * Used by: AuthCallback, GoogleAuthCallback, MicrosoftAuthCallback
 * 
 * Supports:
 * - Stripe checkout redirect for paid plans
 * - New user signup flow with pricing selection
 * - Existing user detection for signup flow
 * - Admin role detection for dashboard routing
 * - Multi-org support and org access validation
 * - Removed user handling with org creation option
 */

import { toast } from 'sonner';

/**
 * Result types from OAuth processing
 */
export const CallbackResultType = {
  REDIRECT_CHECKOUT: 'redirect_checkout',      // Redirect to Stripe checkout
  REDIRECT_PRICING: 'redirect_pricing',        // New user needs to select plan
  REDIRECT_ONBOARDING: 'redirect_onboarding',  // New user needs onboarding
  REDIRECT_ACCESS: 'redirect_access',          // Access validation redirect
  REDIRECT_ADMIN: 'redirect_admin',            // Admin user
  REDIRECT_DASHBOARD: 'redirect_dashboard',    // Regular user
  REDIRECT_INVITE: 'redirect_invite',          // Pending invite
  ERROR: 'error',                              // Error occurred
};

/**
 * Process OAuth callback data and determine the correct navigation path.
 * 
 * @param {Object} params - Callback parameters
 * @param {string} params.sessionToken - The session token from OAuth
 * @param {boolean} params.isNewUser - Whether this is a new user
 * @param {string} params.userEmail - User's email
 * @param {string} params.userRole - User's role (admin/member)
 * @param {boolean} params.onboardingCompleted - Whether onboarding is done
 * @param {boolean} params.existingUserFromSignup - Existing user came through signup
 * @param {string} params.checkoutUrl - Stripe checkout URL (if paid plan)
 * @param {string} params.accessRedirect - Access validation redirect path
 * @param {string} params.accessMessage - Access message to display
 * @param {string} params.removedFromOrg - Org user was removed from
 * @param {string} params.availableOrgsJson - JSON string of available orgs
 * @param {Object} params.user - Full user object (optional)
 * @param {Object} params.organization - Full organization object (optional)
 * 
 * @returns {Object} Result with type, path, and any additional data
 */
export function processOAuthCallback(params) {
  const {
    sessionToken,
    isNewUser = false,
    userEmail = '',
    userRole = 'member',
    onboardingCompleted = false,
    existingUserFromSignup = false,
    checkoutUrl = null,
    accessRedirect = null,
    accessMessage = null,
    removedFromOrg = null,
    availableOrgsJson = null,
    user = null,
    organization = null,
    // For pricing redirect (new user without plan)
    oauthSignupToken = null,
    pricingRedirectUrl = null,
  } = params;

  const isAdmin = userRole === 'admin';

  // 1. Check for pending invite — needs explicit acceptance after OAuth
  const pendingInvite = sessionStorage.getItem('pendingInvite');
  if (pendingInvite && sessionToken) {
    sessionStorage.removeItem('pendingInvite');
    return {
      type: CallbackResultType.REDIRECT_INVITE,
      path: `/products`,
      state: { user, organization },
      pendingInviteToken: pendingInvite,
    };
  }

  // 2. New user needs to select a plan (no account created yet)
  if (pricingRedirectUrl || oauthSignupToken) {
    const redirectPath = pricingRedirectUrl || `/pricing?oauth_signup=${oauthSignupToken}`;
    console.log('[OAuthCallback] New user needs to select plan, redirecting to:', redirectPath);
    return {
      type: CallbackResultType.REDIRECT_PRICING,
      path: redirectPath,
      replace: true,
    };
  }

  // 3. Validate we have a session token for remaining flows
  if (!sessionToken) {
    console.error('[OAuthCallback] No session_token provided');
    toast.error('Authentication failed. Please try again.');
    return {
      type: CallbackResultType.ERROR,
      path: '/login',
      error: 'No session token',
    };
  }

  // 4. Paid plan signup - redirect to Stripe checkout
  if (checkoutUrl) {
    console.log('[OAuthCallback] Redirecting to Stripe checkout');
    toast.success('Account created! Redirecting to payment setup...');
    cleanupSignupStorage();
    return {
      type: CallbackResultType.REDIRECT_CHECKOUT,
      url: checkoutUrl,
      external: true,
    };
  }

  // 5. Handle access validation redirects (removed from org, multi-org, etc.)
  if (accessRedirect) {
    console.log('[OAuthCallback] Access redirect required:', accessRedirect);
    
    // Store context for the target page
    if (accessMessage) {
      sessionStorage.setItem('access_message', accessMessage);
    }
    if (removedFromOrg) {
      sessionStorage.setItem('removed_from_org', removedFromOrg);
      toast.warning(`You no longer have access to ${removedFromOrg}`);
    }
    if (availableOrgsJson) {
      sessionStorage.setItem('available_orgs', availableOrgsJson);
    }
    
    return {
      type: CallbackResultType.REDIRECT_ACCESS,
      path: accessRedirect,
      replace: true,
    };
  }

  // 6. Track if this was a signup flow
  const wasSignupFlow = sessionStorage.getItem('is_new_signup') === 'true';
  const wasExplorerSignup = sessionStorage.getItem('explorer_oauth_signup') === 'true';
  cleanupSignupStorage();
  sessionStorage.removeItem('explorer_oauth_signup');

  // 6b. Explorer OAuth signup — go straight to product wizard
  if (wasExplorerSignup && isNewUser) {
    console.log('[OAuthCallback] Explorer OAuth signup, redirecting to product wizard');
    toast.success("Welcome aboard! Let's build your product.");
    return {
      type: CallbackResultType.REDIRECT_DASHBOARD,
      path: '/products/new',
      replace: true,
      state: { user, organization },
    };
  }

  // 7. Handle existing user who came through signup flow
  if (existingUserFromSignup) {
    console.log('[OAuthCallback] Existing user came through signup flow');
    sessionStorage.setItem('show_existing_user_modal', 'true');
    sessionStorage.setItem('existing_user_email', userEmail || '');
    
    return {
      type: isAdmin ? CallbackResultType.REDIRECT_ADMIN : CallbackResultType.REDIRECT_DASHBOARD,
      path: isAdmin ? '/admin' : '/dashboard',
      replace: true,
      state: { user, organization, existingAccountFromSignup: true },
    };
  }

  // 8. New user - send to onboarding (unless admin)
  if (isNewUser && !onboardingCompleted && !isAdmin) {
    console.log('[OAuthCallback] New user, redirecting to onboarding');
    toast.success('Account created successfully!');
    return {
      type: CallbackResultType.REDIRECT_ONBOARDING,
      path: '/onboarding',
      replace: true,
      state: { user, organization },
    };
  }

  // 9. Admin user - go to admin dashboard
  if (isAdmin) {
    console.log('[OAuthCallback] Admin user, redirecting to admin dashboard');
    if (wasSignupFlow) {
      toast.success('Welcome back!');
    }
    return {
      type: CallbackResultType.REDIRECT_ADMIN,
      path: '/admin',
      replace: true,
      state: { user, organization },
    };
  }

  // 10. Regular returning user - go to dashboard
  console.log('[OAuthCallback] Returning user, redirecting to dashboard');
  if (wasSignupFlow) {
    toast.success('Welcome back!');
  } else if (isNewUser) {
    toast.success('Signed in successfully!');
  }
  
  return {
    type: CallbackResultType.REDIRECT_DASHBOARD,
    path: '/dashboard',
    replace: true,
    state: { user, organization },
  };
}

/**
 * Parse URL params from Google/Microsoft OAuth callback
 */
export function parseOAuthUrlParams(searchParams) {
  return {
    sessionToken: searchParams.get('session_token'),
    isNewUser: searchParams.get('is_new_user') === '1',
    userEmail: searchParams.get('user_email'),
    userRole: searchParams.get('user_role'),
    onboardingCompleted: searchParams.get('onboarding_completed') === '1',
    existingUserFromSignup: searchParams.get('existing_user_from_signup') === '1',
    checkoutUrl: searchParams.get('checkout_url'),
    accessRedirect: searchParams.get('access_redirect'),
    accessMessage: searchParams.get('access_message'),
    removedFromOrg: searchParams.get('removed_from_org'),
    availableOrgsJson: searchParams.get('available_orgs'),
    error: searchParams.get('error'),
    errorMessage: searchParams.get('message'),
  };
}

/**
 * Execute the navigation based on callback result.
 * If there's a pending invite, accept it first before navigating.
 */
export async function executeCallbackNavigation(result, navigate, apiBaseUrl) {
  // If this result carries a pending invite token, accept it before navigating
  if (result.pendingInviteToken && apiBaseUrl) {
    try {
      const resp = await fetch(`${apiBaseUrl}/api/onboarding/invite/${result.pendingInviteToken}/accept`, {
        method: 'POST',
        credentials: 'include',
      });
      if (resp.ok) {
        console.log('[OAuthCallback] Invite accepted successfully');
      } else {
        console.warn('[OAuthCallback] Invite accept returned', resp.status);
      }
    } catch (err) {
      console.warn('[OAuthCallback] Invite accept failed:', err);
    }
  }

  if (result.external && result.url) {
    // External redirect (e.g., Stripe checkout)
    window.location.href = result.url;
    return;
  }

  if (result.path) {
    navigate(result.path, {
      replace: result.replace ?? true,
      state: result.state,
    });
  }
}

/**
 * Clean up signup-related session storage
 */
function cleanupSignupStorage() {
  sessionStorage.removeItem('is_new_signup');
  sessionStorage.removeItem('signup_pricing');
  sessionStorage.removeItem('signup_safe_mode');
}

/**
 * Parse 402 Payment Required response from Emergent OAuth
 * Returns the redirect URL for pricing page with OAuth token
 */
export function parse402Response(responseData) {
  // FastAPI HTTPException wraps detail in response body
  // Structure: { "detail": { "error": "...", "redirect_url": "..." } }
  
  console.log('[parse402Response] Input:', JSON.stringify(responseData));
  console.log('[parse402Response] Input type:', typeof responseData);
  
  if (!responseData) {
    console.log('[parse402Response] FAIL: responseData is falsy');
    return null;
  }
  
  const detail = responseData.detail;
  console.log('[parse402Response] detail:', JSON.stringify(detail));
  console.log('[parse402Response] detail type:', typeof detail);
  
  if (!detail) {
    console.log('[parse402Response] FAIL: detail is falsy');
    return null;
  }
  
  // If detail is a string (sometimes FastAPI does this), try to parse it
  let parsedDetail = detail;
  if (typeof detail === 'string') {
    console.log('[parse402Response] detail is string, attempting to parse as JSON');
    try {
      parsedDetail = JSON.parse(detail);
      console.log('[parse402Response] parsed detail:', JSON.stringify(parsedDetail));
    } catch (e) {
      console.log('[parse402Response] FAIL: could not parse detail string as JSON:', e.message);
      return null;
    }
  }
  
  console.log('[parse402Response] parsedDetail.error:', parsedDetail.error);
  console.log('[parse402Response] parsedDetail.redirect_url:', parsedDetail.redirect_url);
  
  if (parsedDetail.error === 'signup_required' && parsedDetail.redirect_url) {
    console.log('[parse402Response] SUCCESS: returning redirect_url:', parsedDetail.redirect_url);
    return parsedDetail.redirect_url;
  }
  
  console.log('[parse402Response] FAIL: error !== signup_required OR redirect_url is falsy');
  console.log('[parse402Response] error check:', parsedDetail.error === 'signup_required');
  console.log('[parse402Response] redirect_url truthy:', !!parsedDetail.redirect_url);
  
  return null;
}
